import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Persistent guest id so anonymous work can be migrated on sign-up
export const getGuestId = () => {
  let g = localStorage.getItem("ae_guest_id");
  if (!g) { g = "guest_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("ae_guest_id", g); }
  return g;
};

export const getToken = () => localStorage.getItem("ae_token");
export const setToken = (t) => (t ? localStorage.setItem("ae_token", t) : localStorage.removeItem("ae_token"));

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Extract a human-friendly error message from an axios error. Backend now
// returns structured error details `{stage, reason, message, detail}` for
// upload/extract failures — surface `message` when present. Falls back to
// the previous plain-string `detail` shape used elsewhere.
export const errorMessage = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (d && typeof d === "object" && d.message) return d.message;
  return fallback;
};

export const uploadFile = async (file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (!onProgress) return;
      // evt.total is undefined on some browsers when the request is chunked; skip.
      if (evt.total) {
        onProgress({ loaded: evt.loaded, total: evt.total, percent: Math.round((evt.loaded / evt.total) * 100) });
      } else {
        onProgress({ loaded: evt.loaded, total: 0, percent: null });
      }
    },
  });
  return data;
};

export const startAnalysis = async (payload) => {
  const { data } = await api.post("/analyze", { ...payload, guest_id: getGuestId() });
  return data;
};

export const extractBrief = async (text) => (await api.post("/extract-brief", { text })).data;
export const classifyDoc = async (text, filename) => (await api.post("/classify-doc", { text, filename })).data;
export const improveSection = async (analysis_id, section) => (await api.post("/improve-section", { analysis_id, section })).data;
export const getAnalysis = async (id) => (await api.get(`/analysis/${id}`)).data;
export const listAnalyses = async () => (await api.get(`/analyses`, { params: { guest_id: getGuestId() } })).data;
export const deleteAnalysis = async (id) => (await api.delete(`/analysis/${id}`, { params: { guest_id: getGuestId() } })).data;
export const setActualGrade = async (id, actual_grade) => (await api.post(`/analysis/${id}/actual-grade`, { actual_grade, guest_id: getGuestId() })).data;
export const getAccuracy = async () => (await api.get(`/accuracy`)).data;
export const getAssignmentVersions = async (assignmentId) => (await api.get(`/assignment/${assignmentId}/versions`, { params: { guest_id: getGuestId() } })).data;

// Billing
export const billingStatus = async () => (await api.get(`/billing/status`, { params: { guest_id: getGuestId() } })).data;
export const createCheckout = async (package_id) => (await api.post(`/billing/checkout`, { package_id, origin_url: window.location.origin })).data;
export const checkoutStatus = async (session_id) => (await api.get(`/billing/status/${session_id}`)).data;
export const fetchPublicPlans = async () => (await api.get(`/plans/public`)).data;

// -----------------------------------------------------------------
// Partner attribution (P-B) — single source of truth for partner slug stash
// -----------------------------------------------------------------
const PARTNER_SLUG_KEY = "myae_ref_slug";
const PARTNER_SOURCE_KEY = "myae_ref_source";
const PARTNER_TOUCHED_KEY = "myae_ref_touched_at";

export const stashPartnerSlug = (slug, source = "direct_link") => {
  try {
    if (!slug) return;
    localStorage.setItem(PARTNER_SLUG_KEY, slug);
    localStorage.setItem(PARTNER_SOURCE_KEY, source);
    localStorage.setItem(PARTNER_TOUCHED_KEY, new Date().toISOString());
  } catch {}
};

export const readPartnerSlug = () => {
  try {
    return {
      slug: localStorage.getItem(PARTNER_SLUG_KEY) || "",
      source: localStorage.getItem(PARTNER_SOURCE_KEY) || "direct_link",
    };
  } catch { return { slug: "", source: "" }; }
};

export const clearPartnerSlug = () => {
  try {
    localStorage.removeItem(PARTNER_SLUG_KEY);
    localStorage.removeItem(PARTNER_SOURCE_KEY);
    localStorage.removeItem(PARTNER_TOUCHED_KEY);
  } catch {}
};

/** POST the stashed slug (if any) to the server. Idempotent — the server
 *  overwrites prior attribution on each direct visit. Returns true if a
 *  write was attempted. */
export const flushPartnerAttribution = async () => {
  const { slug, source } = readPartnerSlug();
  if (!slug) return false;
  try {
    const r = await api.post(`/partners/attribute`, { slug, source });
    if (r?.data?.attributed) {
      // Keep the stash so the window logic on the server handles expiry;
      // we still clear the "source" flag so a repeat login doesn't appear
      // as a fresh direct touch.
      localStorage.setItem(PARTNER_SOURCE_KEY, "re_login");
    }
    return !!r?.data?.attributed;
  } catch {
    return false;
  }
};

// Auth
export const registerUser = async (payload) => {
  // Capture ?ref=CODE from the current URL so referrals work end-to-end
  let ref_code = "";
  try {
    const params = new URLSearchParams(window.location.search);
    ref_code = params.get("ref") || localStorage.getItem("myae_ref_code") || "";
    if (params.get("ref")) localStorage.setItem("myae_ref_code", params.get("ref"));
  } catch (e) {}
  return (await api.post("/auth/register", { ...payload, guest_id: getGuestId(), ref_code })).data;
};
export const loginUser = async (payload) => (await api.post("/auth/login", { ...payload, guest_id: getGuestId() })).data;
export const googleSession = async (session_id) => (await api.post("/auth/google/session", { session_id, guest_id: getGuestId() })).data;
export const fetchMe = async () => (await api.get("/auth/me")).data;
export const logoutUser = async () => (await api.post("/auth/logout")).data;
export const fetchProfile = async () => (await api.get("/profile")).data;

// Public settings (launch promo mode etc.) — cached in-memory across renders
export const fetchPublicSettings = async () => (await api.get("/settings/public")).data;
// Admin — promo/launch mode
export const getPromoMode = async () => (await api.get("/admin/promo-mode")).data;
export const updatePromoMode = async (payload) => (await api.patch("/admin/promo-mode", payload)).data;

// AI Provenance (Submission Intelligence "Coming Soon" card + admin detectors)
export const fetchProvenanceStatus = async () => (await api.get("/provenance/status")).data;
export const adminListProvenanceDetectors = async () => (await api.get("/admin/provenance/detectors")).data;
export const adminDetectProvenance = async (text, analysis_id) => (await api.post("/admin/provenance/detect", { text, analysis_id })).data;

// Model Benchmark harness (admin-only)
export const benchmarkConfig = async () => (await api.get("/admin/benchmark/config")).data;
export const benchmarkPreflight = async (payload) => (await api.post("/admin/benchmark/preflight", payload)).data;
export const benchmarkListPapers = async () => (await api.get("/admin/benchmark/papers")).data;
export const benchmarkGetPaper = async (paperId) => (await api.get(`/admin/benchmark/papers/${paperId}`)).data;
export const benchmarkAddPaper = async (paper) => (await api.post("/admin/benchmark/papers", paper)).data;
export const benchmarkDeletePaper = async (paperId) => (await api.delete(`/admin/benchmark/papers/${paperId}`)).data;
export const benchmarkRun = async (payload) => (await api.post("/admin/benchmark/run", payload)).data;
export const benchmarkListRuns = async (limit = 20) => (await api.get(`/admin/benchmark/runs?limit=${limit}`)).data;
export const benchmarkRunDetail = async (runId) => (await api.get(`/admin/benchmark/runs/${runId}`)).data;
export const benchmarkRunCsvUrl = (runId) => `${API}/admin/benchmark/runs/${runId}/csv`;

// Admin
export const getAdminStats = async () => (await api.get("/admin/stats")).data;
export const getAdminOcrUsage = async () => (await api.get("/admin/ocr-usage")).data;
export const updateFlags = async (flags) => (await api.post("/admin/flags", flags)).data;
export const getAdminAnalyses = async (params = {}) => (await api.get("/admin/analyses", { params })).data;
export const getAdminDebug = async (id) => (await api.get(`/admin/analysis/${id}/debug`)).data;
export const getAdminUsers = async () => (await api.get("/admin/users")).data;
export const getAdminCalibration = async () => (await api.get("/admin/calibration")).data;
export const setAdminActualGrade = async (id, actual_grade) => (await api.post(`/analysis/${id}/actual-grade`, { actual_grade })).data;
export const createPromo = async (payload) => (await api.post("/admin/promo", payload)).data;
export const listPromo = async (campaign) => (await api.get("/admin/promo", { params: campaign ? { campaign } : {} })).data;
export const togglePromo = async (code, active) => (await api.patch(`/admin/promo/${code}`, { active })).data;
export const deletePromo = async (code) => (await api.delete(`/admin/promo/${code}`)).data;
export const redeemPromo = async (code) => (await api.post("/promo/redeem", { code })).data;
export const grandfatherBatch = async (payload) => (await api.post("/admin/promo/grandfather-batch", payload)).data;

// Auth helpers — verification + password reset
export const resendVerification = async (email) => (await api.post("/auth/resend-verification", { email })).data;
export const forgotPassword = async (email) => (await api.post("/auth/forgot-password", { email })).data;
export const resetPassword = async (token, password) => (await api.post("/auth/reset-password", { token, password })).data;

// Admin — Calibration Centre (Phases 2-4)
export const getCalibrationDashboard = async () => (await api.get("/admin/calibration/dashboard")).data;
export const getCalibrationRecords = async (params = {}) => (await api.get("/admin/calibration/records", { params })).data;
export const createCalibrationRecord = async (payload) => (await api.post("/admin/calibration/records", payload)).data;
export const updateCalibrationRecord = async (id, payload) => (await api.patch(`/admin/calibration/records/${id}`, payload)).data;
export const deleteCalibrationRecord = async (id) => (await api.delete(`/admin/calibration/records/${id}`)).data;
export const getCalibrationSettings = async () => (await api.get("/admin/calibration/settings")).data;
export const setCalibrationSettings = async (payload) => (await api.post("/admin/calibration/settings", payload)).data;

// Admin — Calibration Versioning + Preview + Weekly Cycle + Regression Gate
export const getCalibrationVersions = async () => (await api.get("/admin/calibration/versions")).data;
export const createCalibrationVersion = async (payload) => (await api.post("/admin/calibration/versions", payload)).data;
export const activateCalibrationVersion = async (version) => (await api.post(`/admin/calibration/versions/${version}/activate`)).data;
export const previewCalibration = async (payload) => (await api.post("/admin/calibration/preview", payload)).data;
export const getCalibrationWeekly = async () => (await api.get("/admin/calibration/weekly")).data;
export const getLastRegression = async () => (await api.get("/admin/calibration/regression")).data;
export const runRegression = async () => (await api.post("/admin/calibration/regression", null, { timeout: 200000 })).data;

// Admin — Drift Monitoring + Weekly Ops Report
export const getCalibrationHealth = async () => (await api.get("/admin/calibration/health")).data;
export const getWeeklyOpsReport = async () => (await api.get("/admin/calibration/weekly-report")).data;
export const sendWeeklyOpsReport = async () => (await api.post("/admin/calibration/weekly-report/send")).data;
export const getLastOpsReportSend = async () => (await api.get("/admin/calibration/weekly-report/last")).data;
export const checkDriftAlerts = async () => (await api.post("/admin/calibration/alerts/check")).data;

// Student — Grade Confirmation + Community Rewards
export const confirmGrade = async (analysisId, payload) => (await api.post(`/analyses/${analysisId}/confirm-grade`, payload)).data;
export const submitFeedback = async (payload) => (await api.post("/feedback/submit", payload)).data;
export const getMyCommunity = async () => (await api.get("/me/community")).data;
export const getMyReferral = async () => (await api.get("/me/referral")).data;
export const exchangeRewards = async (kind) => (await api.post("/rewards/exchange", { kind })).data;

// Admin — Rewards
export const getRewardsConfig = async () => (await api.get("/admin/rewards/config")).data;
export const setRewardsConfig = async (payload) => (await api.post("/admin/rewards/config", payload)).data;
export const getRewardsDashboard = async () => (await api.get("/admin/rewards/dashboard")).data;
export const runPrizeDraw = async (payload) => (await api.post("/admin/rewards/draw", payload)).data;
export const getPrizeHistory = async () => (await api.get("/admin/rewards/history")).data;

// Community — Leaderboard + preferences
export const getLeaderboard = async () => (await api.get("/community/leaderboard")).data;
export const setPreferences = async (payload) => (await api.post("/me/preferences", payload)).data;

// Group Assignments
export const listGroups = async () => (await api.get("/groups")).data;
export const createGroup = async (payload) => (await api.post("/groups", payload)).data;
export const getGroup = async (id) => (await api.get(`/groups/${id}`)).data;
export const updateGroup = async (id, payload) => (await api.patch(`/groups/${id}`, payload)).data;
export const deleteGroup = async (id) => (await api.delete(`/groups/${id}`)).data;
export const uploadGroupBrief = async (id, payload) => (await api.post(`/groups/${id}/brief`, payload)).data;
export const clearGroupBrief = async (id) => (await api.delete(`/groups/${id}/brief`)).data;
export const generatePlan = async (id, payload) => (await api.post(`/groups/${id}/plan`, payload || {})).data;
export const applyPlan = async (id, payload) => (await api.post(`/groups/${id}/apply-plan`, payload)).data;
export const importAllocation = async (id, payload) => (await api.post(`/groups/${id}/import-allocation`, payload)).data;
// AI-first contribute flow
export const contribute = async (id, payload) => (await api.post(`/groups/${id}/contribute`, payload)).data;
export const publicInvitePreview = async (token) => (await api.get(`/groups/public-invite/${token}/preview`)).data;
export const joinViaPublicInvite = async (token) => (await api.post(`/groups/public-invite/${token}/join`)).data;
export const inviteToGroup = async (id, payload) => (await api.post(`/groups/${id}/invite`, payload)).data;
export const assignSection = async (id, payload) => (await api.post(`/groups/${id}/assign`, payload)).data;
export const removeMember = async (groupId, memberId) => (await api.delete(`/groups/${groupId}/members/${memberId}`)).data;
export const resetMemberSection = async (groupId, memberId) => (await api.post(`/groups/${groupId}/members/${memberId}/reset`)).data;
export const uploadSection = async (groupId, sectionId, payload) => (await api.post(`/groups/${groupId}/sections/${sectionId}/upload`, payload)).data;
export const listSectionVersions = async (groupId, sectionId) => (await api.get(`/groups/${groupId}/sections/${sectionId}/versions`)).data;
export const removeSectionUpload = async (groupId, sectionId, payload = {}) => (await api.delete(`/groups/${groupId}/sections/${sectionId}/upload`, { data: payload })).data;
export const requestSectionReupload = async (groupId, sectionId, payload = {}) => (await api.post(`/groups/${groupId}/sections/${sectionId}/request-reupload`, payload)).data;
export const restoreSectionVersion = async (groupId, sectionId, versionId) => (await api.post(`/groups/${groupId}/sections/${sectionId}/versions/${versionId}/restore`)).data;
export const moveSectionVersion = async (groupId, sectionId, versionId, targetSectionId, reason = "") => (await api.post(`/groups/${groupId}/sections/${sectionId}/versions/${versionId}/move`, { target_section_id: targetSectionId, reason })).data;
export const analyzeDraft = async (groupId) => (await api.post(`/groups/${groupId}/analyze-draft`)).data;
export const finalizeGroup = async (groupId) => (await api.post(`/groups/${groupId}/finalize`)).data;
export const previewInvite = async (token) => (await api.get(`/groups/invite/${token}/preview`)).data;
export const acceptInvite = async (token) => (await api.post(`/groups/invite/${token}/accept`)).data;
export const groupExportUrl = (groupId, fmt) => `${API}/groups/${groupId}/export/${fmt}?token=${encodeURIComponent(getToken() || "")}`;
export const submissionCheck = async (groupId) => (await api.get(`/groups/${groupId}/submission/check`)).data;
export const submissionPackUrl = (groupId) => `${API}/groups/${groupId}/submission/pack?token=${encodeURIComponent(getToken() || "")}`;
export const submissionPack = async (groupId) => {
  const res = await api.post(`/groups/${groupId}/submission/pack`, {}, { responseType: "blob" });
  return res;
};
export const setGroupLock = async (groupId, locked) => (await api.post(`/groups/${groupId}/lock`, { locked })).data;
export const projectLead = async (groupId, force = false) => (await api.get(`/groups/${groupId}/project-lead${force ? '?force=true' : ''}`)).data;
export const whatsappPreview = async (payload) => (await api.post(`/groups/whatsapp-preview`, payload)).data;
export const improveThis = async (analysisId, payload) => (await api.post(`/analysis/${analysisId}/improve-this`, payload)).data;
export const fetchReportWording = async () => (await api.get(`/report-wording`)).data;
export const adminGetWording = async () => (await api.get(`/admin/report-wording`)).data;
export const adminSaveWording = async (payload) => (await api.post(`/admin/report-wording`, payload)).data;
export const suggestStructure = async (payload) => (await api.post(`/structure/suggest`, payload)).data;
export const analysisSuggestStructure = async (id) => (await api.post(`/analysis/${id}/suggest-structure`)).data;
